import type {
  LandingProductCallbacks,
  LandingProductSnapshot,
} from './buildLandingProductDescriptor';
import type { FmProductSectionState } from '@/components/ui/FmProductPage';

export function formatLandingDate(value: string, locale: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
}

export function landingRootState(
  snapshot: LandingProductSnapshot,
  callbacks: LandingProductCallbacks,
  id: string,
): FmProductSectionState {
  if (snapshot.loading) {
    return { status: 'loading', label: snapshot.copy.state.loading };
  }
  if (snapshot.error && !snapshot.story) {
    return {
      status: 'error',
      label: snapshot.copy.state.error,
      detail: snapshot.error,
      retry: {
        id: `${id}-retry`,
        label: snapshot.copy.state.retry,
        intent: 'secondary',
        onSelect: callbacks.retry,
      },
    };
  }
  if (!snapshot.story) {
    return {
      status: 'empty',
      label: snapshot.copy.state.empty,
      detail: snapshot.copy.state.locked,
    };
  }
  if (snapshot.story.firstPicture.status === 'pending') {
    // The sources are still being read. Say so; do not fall through to the
    // error branch (nothing has failed) and do not render a fact we do not
    // have. The query re-polls until the build behind this answer completes.
    return { status: 'loading', label: snapshot.copy.state.loading };
  }
  if (snapshot.story.firstPicture.status === 'unavailable') {
    return {
      status: 'error',
      label: snapshot.copy.state.error,
      detail: snapshot.copy.state.sourceUnavailableDetail,
      retry: {
        id: `${id}-retry`,
        label: snapshot.copy.state.retry,
        intent: 'secondary',
        onSelect: callbacks.retry,
      },
    };
  }
  if (snapshot.refreshing) {
    return {
      status: 'stale',
      label: snapshot.copy.state.stale,
      detail: snapshot.copy.state.partial,
      updatedAt: formatLandingDate(snapshot.story.generatedAt, snapshot.locale),
    };
  }
  return { status: 'ready' };
}
